import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Wallet, Plus, RefreshCw, Receipt, ArrowDownCircle,
  Eye, Trash2, AlertCircle, CheckCircle2, Clock, Ban,
  PiggyBank, RotateCcw, ChevronLeft, FileText, BarChart3, TrendingUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../../../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "../../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../../components/ui/select";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TENANT_ID = "cao";

const COLORS = ["#3b82f6","#10b981","#f59e0b","#ef4444","#8b5cf6","#ec4899","#06b6d4","#f97316"];

const formatCurrency = (val) => {
  const n = parseFloat(val) || 0;
  return n.toLocaleString("es-AR", { style: "currency", currency: "ARS", minimumFractionDigits: 2 });
};

const formatK = (val) => {
  if (val >= 1000) return `$${(val / 1000).toFixed(0)}k`;
  return `$${val}`;
};

const MES_NOMBRES = {
  "01": "Ene", "02": "Feb", "03": "Mar", "04": "Abr", "05": "May", "06": "Jun",
  "07": "Jul", "08": "Ago", "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dic"
};

const FondoFijo = () => {
  const [loading, setLoading] = useState(true);
  const [fondos, setFondos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [resumen, setResumen] = useState(null);

  const [vista, setVista] = useState("lista");
  const [fondoActual, setFondoActual] = useState(null);
  const [gastos, setGastos] = useState([]);
  const [reposiciones, setReposiciones] = useState([]);
  const [tabDetalle, setTabDetalle] = useState("gastos");
  const [dashboardData, setDashboardData] = useState(null);

  const [modalFondo, setModalFondo] = useState(false);
  const [modalGasto, setModalGasto] = useState(false);
  const [modalReposicion, setModalReposicion] = useState(false);
  const [modalDetalleRepo, setModalDetalleRepo] = useState(false);
  const [repoDetalle, setRepoDetalle] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formFondo, setFormFondo] = useState({
    codigo: "", nombre: "", monto_asignado: "", responsable: "",
    cuenta_tesoreria_id: "", observaciones: ""
  });
  const [formGasto, setFormGasto] = useState({
    fecha: new Date().toISOString().split("T")[0],
    categoria_id: "", concepto: "", monto: "", numero_comprobante: "", observaciones: ""
  });
  const [formRepo, setFormRepo] = useState({
    fecha: new Date().toISOString().split("T")[0],
    cuenta_origen_id: "", observaciones: ""
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [fondosRes, catsRes, cuentasRes, resumenRes, dashRes] = await Promise.all([
        axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/fondos-fijos`),
        axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/categorias-gasto-ff`),
        axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/cuentas-tesoreria`),
        axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/fondos-fijos-resumen`),
        axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/fondos-fijos-dashboard`)
      ]);
      setFondos(fondosRes.data);
      setCategorias(catsRes.data);
      setCuentas(cuentasRes.data);
      setResumen(resumenRes.data);
      setDashboardData(dashRes.data);
    } catch (err) {
      console.error(err);
      toast.error("Error cargando datos");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const abrirDetalle = async (fondo) => {
    setFondoActual(fondo);
    setVista("detalle");
    setTabDetalle("gastos");
    await fetchDetalles(fondo.id);
  };

  const fetchDetalles = async (fondoId) => {
    try {
      const [gastosRes, reposRes] = await Promise.all([
        axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/fondos-fijos/${fondoId}/gastos`),
        axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/fondos-fijos/${fondoId}/reposiciones`)
      ]);
      setGastos(gastosRes.data);
      setReposiciones(reposRes.data);
      const fondoRes = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/fondos-fijos/${fondoId}`);
      setFondoActual(fondoRes.data);
    } catch (err) { console.error(err); }
  };

  const handleCrearFondo = async () => {
    if (!formFondo.codigo || !formFondo.nombre || !formFondo.monto_asignado || !formFondo.responsable) {
      toast.error("Complete los campos obligatorios"); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/erp/empresas/${TENANT_ID}/fondos-fijos`, {
        ...formFondo, monto_asignado: parseFloat(formFondo.monto_asignado),
        cuenta_tesoreria_id: formFondo.cuenta_tesoreria_id ? parseInt(formFondo.cuenta_tesoreria_id) : null
      });
      toast.success("Fondo fijo creado");
      setModalFondo(false);
      setFormFondo({ codigo: "", nombre: "", monto_asignado: "", responsable: "", cuenta_tesoreria_id: "", observaciones: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Error creando fondo"); }
    setSaving(false);
  };

  const handleRegistrarGasto = async () => {
    if (!formGasto.concepto || !formGasto.monto || !formGasto.fecha) {
      toast.error("Complete los campos obligatorios"); return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/erp/empresas/${TENANT_ID}/fondos-fijos/${fondoActual.id}/gastos`,
        { ...formGasto, monto: parseFloat(formGasto.monto),
          categoria_id: formGasto.categoria_id ? parseInt(formGasto.categoria_id) : null }
      );
      toast.success(`Gasto registrado. Saldo: ${formatCurrency(res.data.saldo_disponible)}`);
      setModalGasto(false);
      setFormGasto({ fecha: new Date().toISOString().split("T")[0], categoria_id: "", concepto: "", monto: "", numero_comprobante: "", observaciones: "" });
      await fetchDetalles(fondoActual.id);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Error registrando gasto"); }
    setSaving(false);
  };

  const handleAnularGasto = async (gastoId) => {
    if (!window.confirm("¿Anular este gasto?")) return;
    try {
      await axios.put(`${API_URL}/api/erp/empresas/${TENANT_ID}/fondos-fijos/${fondoActual.id}/gastos/${gastoId}/anular`);
      toast.success("Gasto anulado");
      await fetchDetalles(fondoActual.id);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Error anulando gasto"); }
  };

  const handleReponer = async () => {
    if (!formRepo.fecha) { toast.error("Indique la fecha"); return; }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/erp/empresas/${TENANT_ID}/fondos-fijos/${fondoActual.id}/reponer`,
        { ...formRepo, cuenta_origen_id: formRepo.cuenta_origen_id ? parseInt(formRepo.cuenta_origen_id) : null,
          generar_asiento: true }
      );
      const msg = `Reposición: ${formatCurrency(res.data.monto_total)} (${res.data.gastos_rendidos} gastos)`;
      if (res.data.asiento_id) toast.success(`${msg} - Asiento #${res.data.asiento_id} generado`);
      else toast.success(msg);
      setModalReposicion(false);
      setFormRepo({ fecha: new Date().toISOString().split("T")[0], cuenta_origen_id: "", observaciones: "" });
      await fetchDetalles(fondoActual.id);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Error en reposición"); }
    setSaving(false);
  };

  const verDetalleRepo = async (repoId) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/erp/empresas/${TENANT_ID}/fondos-fijos/${fondoActual.id}/reposiciones/${repoId}/detalle`
      );
      setRepoDetalle(res.data);
      setModalDetalleRepo(true);
    } catch (err) { toast.error("Error cargando detalle"); }
  };

  const porcentajeUso = fondoActual
    ? ((fondoActual.total_gastado_pendiente / fondoActual.monto_asignado) * 100) : 0;

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
          <p className="font-medium text-slate-800">{payload[0].name || payload[0].payload.categoria || payload[0].payload.mes}</p>
          <p className="text-blue-600 font-mono">{formatCurrency(payload[0].value)}</p>
          {payload[0].payload.cantidad && <p className="text-slate-500">{payload[0].payload.cantidad} gastos</p>}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="fondo-fijo-loading">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // === VISTA DETALLE ===
  if (vista === "detalle" && fondoActual) {
    return (
      <div className="space-y-6 p-2" data-testid="fondo-fijo-detalle">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => { setVista("lista"); fetchAll(); }} data-testid="btn-volver-lista">
              <ChevronLeft className="h-4 w-4 mr-1" /> Volver
            </Button>
            <div>
              <h2 className="text-xl font-bold text-slate-800">{fondoActual.nombre}</h2>
              <p className="text-sm text-slate-500">Responsable: {fondoActual.responsable} | Código: {fondoActual.codigo}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setModalGasto(true)} data-testid="btn-nuevo-gasto">
              <Receipt className="h-4 w-4 mr-1" /> Nuevo Gasto
            </Button>
            <Button size="sm" variant="outline" onClick={() => setModalReposicion(true)}
              disabled={fondoActual.gastos_pendientes === 0} data-testid="btn-reponer">
              <RotateCcw className="h-4 w-4 mr-1" /> Reponer
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card><CardContent className="pt-4 pb-3">
            <p className="text-xs text-slate-500 mb-1">Monto Asignado</p>
            <p className="text-lg font-bold text-slate-800" data-testid="monto-asignado">{formatCurrency(fondoActual.monto_asignado)}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4 pb-3">
            <p className="text-xs text-slate-500 mb-1">Gastado Pendiente</p>
            <p className="text-lg font-bold text-red-600" data-testid="gastado-pendiente">{formatCurrency(fondoActual.total_gastado_pendiente)}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4 pb-3">
            <p className="text-xs text-slate-500 mb-1">Saldo Disponible</p>
            <p className={`text-lg font-bold ${fondoActual.saldo_disponible < fondoActual.monto_asignado * 0.2 ? 'text-orange-600' : 'text-emerald-600'}`} data-testid="saldo-disponible">
              {formatCurrency(fondoActual.saldo_disponible)}
            </p>
          </CardContent></Card>
          <Card><CardContent className="pt-4 pb-3">
            <p className="text-xs text-slate-500 mb-1">Uso del Fondo</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-slate-200 rounded-full h-2.5">
                <div className={`h-2.5 rounded-full transition-all ${porcentajeUso > 80 ? 'bg-red-500' : porcentajeUso > 50 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.min(porcentajeUso, 100)}%` }} />
              </div>
              <span className="text-sm font-medium text-slate-600">{porcentajeUso.toFixed(0)}%</span>
            </div>
          </CardContent></Card>
        </div>

        <div className="flex gap-2 border-b pb-2">
          <Button variant={tabDetalle === "gastos" ? "default" : "ghost"} size="sm" onClick={() => setTabDetalle("gastos")} data-testid="tab-gastos">
            <Receipt className="h-4 w-4 mr-1" /> Gastos ({gastos.length})
          </Button>
          <Button variant={tabDetalle === "reposiciones" ? "default" : "ghost"} size="sm" onClick={() => setTabDetalle("reposiciones")} data-testid="tab-reposiciones">
            <RotateCcw className="h-4 w-4 mr-1" /> Reposiciones ({reposiciones.length})
          </Button>
        </div>

        {tabDetalle === "gastos" && (
          <Card><CardContent className="p-0">
            {gastos.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Receipt className="h-10 w-10 mx-auto mb-2 opacity-40" /><p>No hay gastos registrados</p>
              </div>
            ) : (
              <Table data-testid="tabla-gastos">
                <TableHeader><TableRow>
                  <TableHead>Fecha</TableHead><TableHead>Categoría</TableHead>
                  <TableHead>Concepto</TableHead><TableHead>Comprobante</TableHead>
                  <TableHead className="text-right">Monto</TableHead><TableHead>Estado</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {gastos.map((g) => (
                    <TableRow key={g.id}>
                      <TableCell className="text-sm">{g.fecha}</TableCell>
                      <TableCell>{g.categoria_nombre ? <Badge variant="outline" className="text-xs">{g.categoria_nombre}</Badge> : <span className="text-slate-400 text-xs">Sin cat.</span>}</TableCell>
                      <TableCell className="font-medium text-sm">{g.concepto}</TableCell>
                      <TableCell className="text-xs text-slate-500">{g.numero_comprobante || "-"}</TableCell>
                      <TableCell className="text-right font-mono font-medium text-sm">{formatCurrency(g.monto)}</TableCell>
                      <TableCell>
                        {g.anulado ? <Badge variant="destructive" className="text-xs">Anulado</Badge>
                         : g.reposicion_id ? <Badge className="bg-emerald-100 text-emerald-700 text-xs">Rendido</Badge>
                         : <Badge className="bg-amber-100 text-amber-700 text-xs">Pendiente</Badge>}
                      </TableCell>
                      <TableCell>
                        {!g.anulado && !g.reposicion_id && (
                          <Button variant="ghost" size="sm" onClick={() => handleAnularGasto(g.id)} data-testid={`btn-anular-${g.id}`}>
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        )}

        {tabDetalle === "reposiciones" && (
          <Card><CardContent className="p-0">
            {reposiciones.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <RotateCcw className="h-10 w-10 mx-auto mb-2 opacity-40" /><p>No hay reposiciones</p>
              </div>
            ) : (
              <Table data-testid="tabla-reposiciones">
                <TableHeader><TableRow>
                  <TableHead>#</TableHead><TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Monto Total</TableHead>
                  <TableHead className="text-center">Gastos</TableHead>
                  <TableHead>Asiento</TableHead><TableHead>Observaciones</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {reposiciones.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-sm">{r.id}</TableCell>
                      <TableCell className="text-sm">{r.fecha}</TableCell>
                      <TableCell className="text-right font-mono font-medium">{formatCurrency(r.monto_total)}</TableCell>
                      <TableCell className="text-center">{r.cantidad_gastos}</TableCell>
                      <TableCell>{r.asiento_id ? <Badge className="bg-blue-100 text-blue-700 text-xs">Asiento #{r.asiento_id}</Badge> : <span className="text-slate-400 text-xs">-</span>}</TableCell>
                      <TableCell className="text-xs text-slate-500 max-w-[200px] truncate">{r.observaciones || "-"}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => verDetalleRepo(r.id)} data-testid={`btn-ver-repo-${r.id}`}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        )}

        {/* MODALES */}
        <Dialog open={modalGasto} onOpenChange={setModalGasto}>
          <DialogContent className="max-w-md" data-testid="modal-nuevo-gasto">
            <DialogHeader>
              <DialogTitle>Registrar Gasto</DialogTitle>
              <DialogDescription>Saldo disponible: {formatCurrency(fondoActual.saldo_disponible)}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Fecha *</Label><Input type="date" value={formGasto.fecha} onChange={(e) => setFormGasto({ ...formGasto, fecha: e.target.value })} data-testid="input-gasto-fecha" /></div>
              <div><Label>Categoría</Label>
                <Select value={formGasto.categoria_id} onValueChange={(v) => setFormGasto({ ...formGasto, categoria_id: v })}>
                  <SelectTrigger data-testid="select-gasto-categoria"><SelectValue placeholder="Seleccionar categoría" /></SelectTrigger>
                  <SelectContent>{categorias.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.nombre}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Concepto *</Label><Input value={formGasto.concepto} onChange={(e) => setFormGasto({ ...formGasto, concepto: e.target.value })} placeholder="Descripción del gasto" data-testid="input-gasto-concepto" /></div>
              <div><Label>Monto *</Label><Input type="number" step="0.01" value={formGasto.monto} onChange={(e) => setFormGasto({ ...formGasto, monto: e.target.value })} placeholder="0.00" data-testid="input-gasto-monto" /></div>
              <div><Label>N° Comprobante</Label><Input value={formGasto.numero_comprobante} onChange={(e) => setFormGasto({ ...formGasto, numero_comprobante: e.target.value })} placeholder="FC-001234" data-testid="input-gasto-comprobante" /></div>
              <div><Label>Observaciones</Label><Textarea value={formGasto.observaciones} onChange={(e) => setFormGasto({ ...formGasto, observaciones: e.target.value })} rows={2} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setModalGasto(false)}>Cancelar</Button>
              <Button onClick={handleRegistrarGasto} disabled={saving} data-testid="btn-guardar-gasto">
                {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Receipt className="h-4 w-4 mr-1" />} Registrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={modalReposicion} onOpenChange={setModalReposicion}>
          <DialogContent className="max-w-md" data-testid="modal-reposicion">
            <DialogHeader>
              <DialogTitle>Reponer Fondo Fijo</DialogTitle>
              <DialogDescription>
                Se rendirán {fondoActual.gastos_pendientes} gastos por {formatCurrency(fondoActual.total_gastado_pendiente)}.
                Se generará automáticamente el asiento contable.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
              <AlertCircle className="h-4 w-4 inline mr-1" />
              En una futura versión se podrá configurar un flujo de aprobación para este proceso.
            </div>
            <div className="space-y-3">
              <div><Label>Fecha *</Label><Input type="date" value={formRepo.fecha} onChange={(e) => setFormRepo({ ...formRepo, fecha: e.target.value })} data-testid="input-repo-fecha" /></div>
              <div><Label>Cuenta Origen</Label>
                <Select value={formRepo.cuenta_origen_id} onValueChange={(v) => setFormRepo({ ...formRepo, cuenta_origen_id: v })}>
                  <SelectTrigger data-testid="select-repo-cuenta"><SelectValue placeholder="Seleccionar cuenta" /></SelectTrigger>
                  <SelectContent>{cuentas.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.nombre}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Observaciones</Label><Textarea value={formRepo.observaciones} onChange={(e) => setFormRepo({ ...formRepo, observaciones: e.target.value })} rows={2} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setModalReposicion(false)}>Cancelar</Button>
              <Button onClick={handleReponer} disabled={saving} data-testid="btn-confirmar-reposicion">
                {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <RotateCcw className="h-4 w-4 mr-1" />} Confirmar Reposición
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={modalDetalleRepo} onOpenChange={setModalDetalleRepo}>
          <DialogContent className="max-w-lg" data-testid="modal-detalle-reposicion">
            <DialogHeader>
              <DialogTitle>Detalle de Reposición #{repoDetalle?.id}</DialogTitle>
              <DialogDescription>Fecha: {repoDetalle?.fecha} | Total: {formatCurrency(repoDetalle?.monto_total)}</DialogDescription>
            </DialogHeader>
            {repoDetalle?.gastos && (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Fecha</TableHead><TableHead>Categoría</TableHead>
                  <TableHead>Concepto</TableHead><TableHead className="text-right">Monto</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {repoDetalle.gastos.map((g) => (
                    <TableRow key={g.id}>
                      <TableCell className="text-sm">{g.fecha}</TableCell>
                      <TableCell className="text-xs">{g.categoria_nombre || "-"}</TableCell>
                      <TableCell className="text-sm">{g.concepto}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(g.monto)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="font-bold bg-slate-50">
                    <TableCell colSpan={3} className="text-right">TOTAL</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(repoDetalle.monto_total)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ============================
  // VISTA LISTA (Principal)
  // ============================
  return (
    <div className="space-y-6 p-2" data-testid="fondo-fijo-lista">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <PiggyBank className="h-7 w-7 text-blue-600" /> Fondo Fijo / Caja Chica
          </h1>
          <p className="text-sm text-slate-500 mt-1">Gestión de fondos fijos para gastos menores</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll} data-testid="btn-refresh"><RefreshCw className="h-4 w-4" /></Button>
          <Button size="sm" onClick={() => setModalFondo(true)} data-testid="btn-nuevo-fondo">
            <Plus className="h-4 w-4 mr-1" /> Nuevo Fondo
          </Button>
        </div>
      </div>

      {/* Tabs: Lista / Dashboard */}
      <div className="flex gap-2 border-b pb-2">
        <Button variant={vista === "lista" ? "default" : "ghost"} size="sm" onClick={() => setVista("lista")} data-testid="tab-lista">
          <PiggyBank className="h-4 w-4 mr-1" /> Fondos
        </Button>
        <Button variant={vista === "dashboard" ? "default" : "ghost"} size="sm" onClick={() => setVista("dashboard")} data-testid="tab-dashboard">
          <BarChart3 className="h-4 w-4 mr-1" /> Dashboard Gastos
        </Button>
      </div>

      {/* DASHBOARD DE GASTOS */}
      {vista === "dashboard" && dashboardData && (
        <div className="space-y-6" data-testid="dashboard-gastos">
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card><CardContent className="pt-4 pb-3">
              <p className="text-xs text-slate-500">Total Gastado</p>
              <p className="text-lg font-bold text-slate-800">{formatCurrency(dashboardData.totales.total_gastado)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-3">
              <p className="text-xs text-slate-500">Total Gastos</p>
              <p className="text-2xl font-bold text-blue-600">{parseInt(dashboardData.totales.total_gastos)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-3">
              <p className="text-xs text-slate-500">Promedio</p>
              <p className="text-lg font-bold text-slate-800">{formatCurrency(dashboardData.totales.promedio_gasto)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-3">
              <p className="text-xs text-slate-500">Gasto Máximo</p>
              <p className="text-lg font-bold text-red-600">{formatCurrency(dashboardData.totales.gasto_maximo)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-3">
              <p className="text-xs text-slate-500">Gasto Mínimo</p>
              <p className="text-lg font-bold text-emerald-600">{formatCurrency(dashboardData.totales.gasto_minimo)}</p>
            </CardContent></Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pie - Por Categoría */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-blue-500" /> Gastos por Categoría
              </CardTitle></CardHeader>
              <CardContent>
                {dashboardData.por_categoria.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={dashboardData.por_categoria} dataKey="total" nameKey="categoria"
                        cx="50%" cy="50%" outerRadius={100} innerRadius={50} paddingAngle={2}
                        label={({ categoria, percent }) => `${categoria.split(' ')[0]} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}>
                        {dashboardData.por_categoria.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-center text-slate-400 py-12">Sin datos</p>}
              </CardContent>
            </Card>

            {/* Bar - Por Mes */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-500" /> Evolución Mensual
              </CardTitle></CardHeader>
              <CardContent>
                {dashboardData.por_mes.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dashboardData.por_mes.map(m => ({
                      ...m,
                      label: MES_NOMBRES[m.mes.split("-")[1]] + " " + m.mes.split("-")[0]
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={formatK} tick={{ fontSize: 12 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="total" name="Total" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-center text-slate-400 py-12">Sin datos</p>}
              </CardContent>
            </Card>
          </div>

          {/* Tabla ranking categorías */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Ranking por Categoría</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table data-testid="tabla-ranking-categorias">
                <TableHeader><TableRow>
                  <TableHead></TableHead><TableHead>Categoría</TableHead>
                  <TableHead className="text-center">Gastos</TableHead>
                  <TableHead className="text-right">Total</TableHead><TableHead>% del Total</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {dashboardData.por_categoria.map((c, i) => {
                    const pct = dashboardData.totales.total_gastado > 0
                      ? ((c.total / dashboardData.totales.total_gastado) * 100) : 0;
                    return (
                      <TableRow key={i}>
                        <TableCell><div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} /></TableCell>
                        <TableCell className="font-medium">{c.categoria}</TableCell>
                        <TableCell className="text-center">{c.cantidad}</TableCell>
                        <TableCell className="text-right font-mono font-medium">{formatCurrency(c.total)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-slate-100 rounded-full h-2 max-w-[120px]">
                              <div className="h-2 rounded-full" style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                            </div>
                            <span className="text-xs text-slate-600 w-10 text-right">{pct.toFixed(1)}%</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Top Gastos */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Top 10 Gastos</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table data-testid="tabla-top-gastos">
                <TableHeader><TableRow>
                  <TableHead>#</TableHead><TableHead>Fecha</TableHead>
                  <TableHead>Concepto</TableHead><TableHead>Categoría</TableHead>
                  <TableHead>Fondo</TableHead><TableHead className="text-right">Monto</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {dashboardData.top_gastos.map((g, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-sm text-slate-400">{i + 1}</TableCell>
                      <TableCell className="text-sm">{g.fecha}</TableCell>
                      <TableCell className="font-medium text-sm">{g.concepto}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{g.categoria}</Badge></TableCell>
                      <TableCell className="text-sm text-slate-500">{g.fondo}</TableCell>
                      <TableCell className="text-right font-mono font-medium">{formatCurrency(g.monto)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* LISTA DE FONDOS */}
      {vista === "lista" && (
        <>
          {resumen && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card><CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div><p className="text-xs text-slate-500">Fondos Activos</p>
                    <p className="text-2xl font-bold text-slate-800" data-testid="resumen-fondos-activos">{resumen.fondos_activos || 0}</p></div>
                  <PiggyBank className="h-8 w-8 text-blue-500 opacity-60" />
                </div>
              </CardContent></Card>
              <Card><CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div><p className="text-xs text-slate-500">Total Asignado</p>
                    <p className="text-lg font-bold text-slate-800">{formatCurrency(resumen.monto_total_asignado)}</p></div>
                  <Wallet className="h-8 w-8 text-emerald-500 opacity-60" />
                </div>
              </CardContent></Card>
              <Card><CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div><p className="text-xs text-slate-500">Pendiente Reposición</p>
                    <p className="text-lg font-bold text-amber-600">{formatCurrency(resumen.total_pendiente_reposicion)}</p></div>
                  <Clock className="h-8 w-8 text-amber-500 opacity-60" />
                </div>
              </CardContent></Card>
              <Card><CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div><p className="text-xs text-slate-500">Total Repuesto</p>
                    <p className="text-lg font-bold text-emerald-600">{formatCurrency(resumen.total_repuesto)}</p></div>
                  <CheckCircle2 className="h-8 w-8 text-emerald-500 opacity-60" />
                </div>
              </CardContent></Card>
            </div>
          )}

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Fondos Fijos</CardTitle></CardHeader>
            <CardContent className="p-0">
              {fondos.length === 0 ? (
                <div className="text-center py-16 text-slate-500">
                  <PiggyBank className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No hay fondos fijos creados</p>
                  <p className="text-sm mt-1">Creá el primer fondo para empezar</p>
                </div>
              ) : (
                <Table data-testid="tabla-fondos">
                  <TableHeader><TableRow>
                    <TableHead>Código</TableHead><TableHead>Nombre</TableHead><TableHead>Responsable</TableHead>
                    <TableHead className="text-right">Asignado</TableHead><TableHead className="text-right">Disponible</TableHead>
                    <TableHead className="text-center">Gastos Pend.</TableHead><TableHead>Estado</TableHead><TableHead></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {fondos.map((f) => (
                      <TableRow key={f.id} className="cursor-pointer hover:bg-slate-50" onClick={() => abrirDetalle(f)} data-testid={`fila-fondo-${f.id}`}>
                        <TableCell className="font-mono text-sm">{f.codigo}</TableCell>
                        <TableCell className="font-medium">{f.nombre}</TableCell>
                        <TableCell className="text-sm text-slate-600">{f.responsable}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{formatCurrency(f.monto_asignado)}</TableCell>
                        <TableCell className="text-right">
                          <span className={`font-mono text-sm font-medium ${f.saldo_disponible < f.monto_asignado * 0.2 ? 'text-red-600' : 'text-emerald-600'}`}>
                            {formatCurrency(f.saldo_disponible)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          {f.gastos_pendientes > 0 ? <Badge className="bg-amber-100 text-amber-700">{f.gastos_pendientes}</Badge> : <span className="text-slate-400">0</span>}
                        </TableCell>
                        <TableCell>
                          <Badge variant={f.estado === "ACTIVO" ? "default" : "secondary"} className={f.estado === "ACTIVO" ? "bg-emerald-100 text-emerald-700" : ""}>
                            {f.estado}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); abrirDetalle(f); }}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Modal Nuevo Fondo */}
      <Dialog open={modalFondo} onOpenChange={setModalFondo}>
        <DialogContent className="max-w-md" data-testid="modal-nuevo-fondo">
          <DialogHeader>
            <DialogTitle>Nuevo Fondo Fijo</DialogTitle>
            <DialogDescription>Crear un nuevo fondo para gastos menores</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Código *</Label><Input value={formFondo.codigo} onChange={(e) => setFormFondo({ ...formFondo, codigo: e.target.value })} placeholder="FF-01" data-testid="input-fondo-codigo" /></div>
              <div><Label>Monto *</Label><Input type="number" step="0.01" value={formFondo.monto_asignado} onChange={(e) => setFormFondo({ ...formFondo, monto_asignado: e.target.value })} placeholder="50000" data-testid="input-fondo-monto" /></div>
            </div>
            <div><Label>Nombre *</Label><Input value={formFondo.nombre} onChange={(e) => setFormFondo({ ...formFondo, nombre: e.target.value })} placeholder="Fondo Oficina Central" data-testid="input-fondo-nombre" /></div>
            <div><Label>Responsable *</Label><Input value={formFondo.responsable} onChange={(e) => setFormFondo({ ...formFondo, responsable: e.target.value })} placeholder="Nombre del responsable" data-testid="input-fondo-responsable" /></div>
            <div><Label>Cuenta Tesorería</Label>
              <Select value={formFondo.cuenta_tesoreria_id} onValueChange={(v) => setFormFondo({ ...formFondo, cuenta_tesoreria_id: v })}>
                <SelectTrigger><SelectValue placeholder="Seleccionar (opcional)" /></SelectTrigger>
                <SelectContent>{cuentas.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.nombre} ({c.tipo})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Observaciones</Label><Textarea value={formFondo.observaciones} onChange={(e) => setFormFondo({ ...formFondo, observaciones: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalFondo(false)}>Cancelar</Button>
            <Button onClick={handleCrearFondo} disabled={saving} data-testid="btn-guardar-fondo">
              {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />} Crear Fondo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FondoFijo;
